import { AccordionV2, Icon, Input } from "@web-app/components";
import { State } from "@web-app/types";

export default function MarketsList({
  includeNullState = false,
  onChangeState,
  searchText,
  sortedStates,
  stateHierarchy,
  statesMap,
  temporaryStateIds,
}: {
  includeNullState?: boolean;
  onChangeState: (value: string, stateId: string) => void;
  searchText: string;
  sortedStates: State[];
  stateHierarchy: Map<string, State[]>;
  statesMap?: Map<string, State>;
  temporaryStateIds: string[];
}) {
  if (sortedStates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center pb-8 pt-4 px-4 text-center">
        <div className="mb-3">
          <Icon
            name="mapMarker"
            className="h-4 lg:h-8 w-4 lg:w-8 text-gray-300"
          />
        </div>
        <h3 className="text-xs lg:text-sm font-medium text-gray-900 mb-1">
          No markets available
        </h3>
        <p className="text-[10px] lg:text-xs text-gray-500">
          {searchText
            ? "No markets match your search."
            : "There are no markets to display."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-y-auto max-h-[250px] lg:max-h-[300px] mb-1 overflow-x-hidden px-3">
      {Array.from(stateHierarchy.entries()).map(
        ([parentId, children], parentIndex, allParents) => {
          const parent = statesMap?.get(parentId);
          if (!parent) return null;

          const isLastParent = parentIndex === allParents.length - 1;

          // If parent has no children, render as regular checkbox
          if (children.length === 0) {
            return (
              <div
                key={parent.id}
                className={`w-full min-w-0 break-words my-1.5 ${isLastParent ? "mb-3" : ""}`}
              >
                <div className="w-full overflow-hidden px-1">
                  <Input
                    type="checkbox"
                    rightLabel={parent.name}
                    value={
                      temporaryStateIds.includes(parent.id) ? "true" : "false"
                    }
                    onChange={(value) => onChangeState(value, parent.id)}
                    wrapText
                  />
                </div>
              </div>
            );
          }

          // If parent has children, render as accordion
          return (
            <AccordionV2
              key={parent.id}
              isBorderless
              noBottomBorder
              className={isLastParent ? "mb-2" : ""}
            >
              <AccordionV2.Label labelClassName="my-2" textLight>
                <div className="flex items-center gap-2 w-full px-1">
                  <Input
                    type="checkbox"
                    rightLabel={parent.name}
                    value={
                      temporaryStateIds.includes(parent.id) ? "true" : "false"
                    }
                    onChange={(value) => onChangeState(value, parent.id)}
                  />
                </div>
              </AccordionV2.Label>
              <AccordionV2.Content>
                <div className="flex flex-col pl-6">
                  {children.map((child) => (
                    <div
                      key={child.id}
                      className="w-full min-w-0 break-words my-1.5"
                    >
                      <div className="w-full overflow-hidden">
                        <Input
                          type="checkbox"
                          rightLabel={child.name}
                          value={
                            temporaryStateIds.includes(child.id)
                              ? "true"
                              : "false"
                          }
                          onChange={(value) => onChangeState(value, child.id)}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionV2.Content>
            </AccordionV2>
          );
        },
      )}
      {includeNullState && (
        <div className="w-full min-w-0 break-words px-1 mb-3">
          <div className="w-full overflow-hidden">
            <Input
              type="checkbox"
              rightLabel="No Markets"
              value={temporaryStateIds.includes("null") ? "true" : "false"}
              onChange={(value) => onChangeState(value, "null")}
            />
          </div>
        </div>
      )}
    </div>
  );
}
